import React, { useState } from 'react';

import { Fade, Flex, Link, Button } from '@chakra-ui/react';

import { CartButton } from './CartButton';
import { NewHeaderActions } from './NewHeaderActions';
import { NewMobileNavModal } from './NewMobileNavModal';
import { NewNavNavigation } from './NewNavNavigation';
import { NewNavSearch } from './NewNavSearch';
import { IAuth } from '../../../Auth/types/auth';
import { useIsMobile } from '../../../hooks';
import { FanaticsCollectLogo, MenuIcon } from '../../../Icons';
import { FanaticsIcon } from '../../../Icons/FanaticsIcon';
import { useModalContext } from '../../../ModalProvider';
import { EColor } from '../../../Theme';
import { tracker } from '../../../utilities/tracker';
import { getLink } from '../../utils/monolithUrls';
import { ProfileDrawer } from '../ProfileDrawer';

export interface IBONavbarProps {
  auth?: IAuth;
  manageAccountUrl: string | undefined;
  handleToggle: () => void;
  isInitializing: boolean;
  show: boolean;
  closeMenu: () => void;
  showCartButton?: boolean;
  enableOfferNotificationBadges?: boolean;
  navRef: React.RefObject<HTMLDivElement>;
  enableCollections?: boolean;
}

export function BONavbar({
  auth,
  manageAccountUrl,
  handleToggle,
  isInitializing,
  show,
  closeMenu,
  showCartButton,
  enableOfferNotificationBadges,
  navRef,
  enableCollections,
}: IBONavbarProps) {
  const { profile } = auth || {};
  const isMobile = useIsMobile('sm');
  const isXl = useIsMobile('xl');
  const isTablet = useIsMobile('lg');
  const { fanaticsCollectBaseUrl } = useModalContext();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const [distanceFromTop, setDistanceFromTop] = React.useState<number>(0);
  const hasUnpaidInvoices = (profile?.unpaidInvoiceCount ?? 0) > 0;

  React.useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        const rect = navRef.current.getBoundingClientRect();
        const bottomDistance = rect.bottom;
        setDistanceFromTop(bottomDistance);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial calculation

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [navRef]);

  const trackAndClose = (sectionClicked: string) => {
    tracker.track('Navigation Click', {
      replatformed: false,
      sectionClicked,
    });
    closeMenu();
  };

  const track = (sectionClicked: string) => {
    tracker.track('Navigation Click', {
      replatformed: false,
      sectionClicked,
    });
  };

  const handleExpandToggle = (isExpanded: boolean) => {
    setIsSearchExpanded(isExpanded);
  };

  const handleCancelSearch = () => {
    setIsSearchExpanded(false);
    tracker.track('Navbar Search Interaction', {
      sectionClicked: 'Search Overlay Cancelled',
    });
  };
  return (
    <Flex
      align="center"
      aria-label="portals"
      justify="space-between"
      maxW="1440px"
      mx="auto"
      padding="16px"
      role="navigation"
      width="100%"
    >
      <Flex
        alignItems="center"
        flex={1}
        gap={{ base: '8px', md: '16px', xl: '32px' }}
        height="44px"
      >
        {/* Logo */}
        <Link
          _hover={{ textDecoration: 'none' }}
          alignItems="center"
          display="flex"
          height="40px"
          href={getLink(fanaticsCollectBaseUrl, 'home')}
          justifyContent="center"
          onClick={() => track('Home')}
        >
          {!isXl ? (
            <FanaticsCollectLogo
              color={EColor.BrandDark}
              height={22}
              mb={!isMobile ? '8px' : '0'}
              width={154}
            />
          ) : (
            <FanaticsIcon height="32px" width="32px" />
          )}
        </Link>

        {/* Search - visible on all screen sizes */}
        <NewNavSearch
          allowCollapsed
          isExpandedFullNav={isSearchExpanded}
          top={distanceFromTop}
          onExpandToggle={handleExpandToggle}
        />

        {/* Cancel button - only when search is expanded on tablet/desktop */}
        {isSearchExpanded && !isMobile && (
          <Button
            _hover={{ backgroundColor: 'transparent' }}
            alignItems="center"
            backgroundColor="transparent"
            border="none"
            color={EColor.Black}
            cursor="pointer"
            display="flex"
            fontSize="16px"
            height="40px"
            justifyContent="center"
            minWidth="fit-content"
            padding={0}
            textTransform="none"
            onClick={handleCancelSearch}
          >
            Cancel
          </Button>
        )}

        {/* Navigation - only when search is not expanded on mobile screens */}
        {!isSearchExpanded && !isMobile && (
          <NewNavNavigation top={distanceFromTop} />
        )}
      </Flex>

      <NewMobileNavModal
        auth={auth}
        closeMenu={closeMenu}
        hasUnpaidInvoices={hasUnpaidInvoices}
        show={show}
        trackAndClose={trackAndClose}
      />

      {/* Actions */}
      {!isSearchExpanded && !isInitializing && (
        <Flex
          alignItems="center"
          as={Fade}
          gap="16px"
          in={!isInitializing}
          justifyContent="flex-end"
        >
          <NewHeaderActions
            auth={auth}
            enableCollections={enableCollections}
            isInitializing={isInitializing}
          />

          {!!showCartButton && profile && (
            <CartButton count={profile.cartCount} />
          )}
          {profile ? (
            <ProfileDrawer
              enableCollections={enableCollections}
              enableOfferNotificationBadges={enableOfferNotificationBadges}
              manageAccountUrl={manageAccountUrl}
              profile={profile}
            />
          ) : null}
          {isTablet && (
            <MenuIcon
              _hover={{ transform: 'scale(1.1)' }}
              boxSize="16px"
              color={EColor.Black}
              cursor="pointer"
              transitionDuration="0.2s"
              onClick={handleToggle}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
}
